import {DepositoryType} from '@transactions/graphql';
import {parseDate, today} from 'common/date';
import Money from 'common/Money';
import {Star} from 'lucide-react';
import {Link} from 'react-router-dom';
import {toDisplayBalance} from 'src/account/balanceDisplay';
import {
  ACCOUNT_TYPE_OPTIONS,
  isAccountClosed,
  isAccountInactive,
} from 'src/AccountsView/accountFilter';
import {Button} from 'src/Button';
import {CardArt} from 'src/CardArt';
import {isCardProductId} from 'src/cardProductOptions';
import {useCurrencyLabel, useMoneyFormatter} from 'src/hooks/useUnits';
import {AccountV2Icon} from 'src/icons/AccountV2Icon';
import {formatListDate} from 'src/lib/displayDate';
import {LogoImage} from 'src/LogoImage';
import {isLogoId} from 'src/logoOptions';
import type {AccountListItemFragment} from '@transactions/graphql';
import type {AccountBalanceInfo} from 'src/AccountsView/accountBalances';

type Props = {
  account: AccountListItemFragment;
  /**
   * This account's balance data (AccountBalances query). Balances arrive
   * after the metadata renders — undefined means "not loaded yet" and shows
   * a placeholder, never a zero.
   */
  balance?: AccountBalanceInfo;
  className?: string;
  /** Rendered inside the row, before the icon (rearrange-mode grip). */
  dragHandle?: React.ReactNode;
  /** Whether this account is pinned to the Starred section. */
  starred?: boolean;
  /** When provided, renders a star toggle button. */
  onToggleStar?: () => void;
  /**
   * Label of the group the row sits in. The type line drops a type that
   * just repeats it (a Credit Card row under "Credit Card").
   */
  groupLabel?: string;
};

// Freshness thresholds for an investment balance observation (balanceAsOf).
// Investments are valued by observation, not reconciliation: recent
// observations render nothing (quiet = healthy, and Plaid-synced accounts
// observe daily), a missed cadence gets a muted "as of" note, and a
// twice-missed monthly snapshot routine turns it amber.
const OBSERVATION_NOTE_DAYS = 14;
const OBSERVATION_STALE_DAYS = 60;

/**
 * Human-readable account type label, from the same option list that drives
 * the Type filter — one place to name a type (adding an enum value without a
 * matching option falls back by role).
 */
const getAccountTypeLabel = (account: AccountListItemFragment): string =>
  ACCOUNT_TYPE_OPTIONS.find((option) => option.matches(account))?.label ??
  (account.__typename === 'InvestmentAccount' ? 'Investment' : 'Account');

/**
 * The row's icon: a credit card wears its face with the issuer badged onto
 * the corner; everything else keeps the institution logo it has always had.
 *
 * Both marks earn their place on a card, because they are not always the same
 * company — Barclays issues the JetBlue card, Chase issues the United ones —
 * and at this size the branding printed on the artwork is far too small to
 * read. So the face says which card it is and the badge says who bills you.
 *
 * A card with no face assigned falls through to the plain logo rather than a
 * card-shaped placeholder: most cards here are dormant and will never have
 * art, and shrinking their logo into an empty tile would cost every one of
 * those rows to make a handful line up.
 */
const RowIcon = ({
  account,
  closed,
}: {
  account: AccountListItemFragment;
  closed: boolean;
}) => {
  const dimmed = closed ? 'opacity-50 grayscale' : '';
  const product =
    account.__typename === 'DepositoryAccount' &&
    account.depositoryType === DepositoryType.CreditCard
      ? account.cardProduct
      : null;

  if (isCardProductId(product)) {
    return (
      <span className={`relative leading-none ${dimmed}`}>
        <CardArt product={product} className="w-10" />
        {/* LogoImage rather than a bare <img>: it already drops empty and
            unknown slugs (which would otherwise request /logos/.png and leave
            a broken-image marker on the artwork) and picks the dark tile for
            the few logos that are light ink on transparency. Ringed in the
            row's own background so the badge reads as a separate object
            resting on the face; the ring follows the row on hover, which is
            why the row is a `group`. */}
        <LogoImage
          logo={account.logo}
          rounded="rounded-[5px]"
          className="ring-card group-hover:ring-accent absolute -bottom-1.5 -left-1.5 size-4 ring-2"
        />
      </span>
    );
  }

  return isLogoId(account.logo) ? (
    <LogoImage
      logo={account.logo}
      rounded="rounded-lg"
      className={`size-8 ${dimmed}`}
    />
  ) : (
    <AccountV2Icon account={account} className={`size-8 shrink-0 ${dimmed}`} />
  );
};

export const AccountRowV2 = ({
  account,
  balance,
  className = '',
  dragHandle,
  starred = false,
  onToggleStar,
  groupLabel,
}: Props) => {
  const formatMoney = useMoneyFormatter();
  const currencyLabel = useCurrencyLabel();
  const closed = isAccountClosed(account);
  // Named in the type line like Closed is: rows surface outside the plain
  // active view (status filter, widened search results, revealed
  // disclosures), where an unmarked dormant account is indistinguishable
  // from a live one. Appears only once the balance data that derives the
  // state has streamed in.
  const inactive = isAccountInactive(account, balance);
  const isCreditCard =
    account.__typename === 'DepositoryAccount' &&
    account.depositoryType === DepositoryType.CreditCard;

  // Age in whole days of the observation backing an investment balance.
  // Null for depository accounts and for investment accounts with no
  // observations (a stored balance with no date has nothing to claim).
  // Closed accounts are exempt: the closing anchor is their final
  // observation by design, so aging it would render a permanent warning
  // about a refresh nobody expects.
  const observationAgeDays =
    account.__typename === 'InvestmentAccount' &&
    !closed &&
    balance?.balanceAsOf != null
      ? today().diff(parseDate(balance.balanceAsOf), 'days').days
      : null;

  // USD is the home currency, so only a foreign currency earns a mention.
  const typeLabel = getAccountTypeLabel(account);
  const meta = [
    typeLabel === groupLabel ? null : typeLabel,
    account.currency === 'USD' ? null : currencyLabel(account.currency),
    closed ? 'Closed' : null,
    inactive ? 'Inactive' : null,
  ].filter((part) => part != null);

  // Among assets (Starred, a connection), a card's positive amount would
  // read as money held, so it says it is owed. Under a Credit Card heading
  // the heading already says so.
  const owedInMixedList =
    isCreditCard &&
    typeLabel !== groupLabel &&
    balance != null &&
    toDisplayBalance(balance.balance, true) > 0;

  const handleToggleStar = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleStar?.();
  };

  // The star toggle is hidden on a phone, where it would crowd the name;
  // the account page's menu stars there. Rearrange mode (a drag handle)
  // keeps it everywhere, since starring is part of arranging.
  return (
    <div
      className={`hover:bg-accent group flex flex-col gap-2 px-3 py-3 transition-colors sm:px-4 ${className}`}
    >
      <div className="flex items-center gap-3">
        {dragHandle}
        <Link
          to={`/accounts/${account._id}`}
          className="flex min-w-0 flex-1 items-center gap-3 no-underline"
        >
          <span className="flex w-10 shrink-0 justify-center">
            <RowIcon account={account} closed={closed} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="flex min-w-0 items-center gap-1">
              <span
                className={`line-clamp-2 text-sm font-medium break-words ${
                  closed ? 'text-muted-foreground' : 'text-foreground'
                }`}
              >
                {account.name}
              </span>
              {starred && !dragHandle && (
                // The star button is hidden on a phone, so the row still
                // says it is starred.
                <Star
                  aria-label="Starred"
                  className="size-3 shrink-0 fill-amber-500 text-amber-500 sm:hidden"
                />
              )}
            </span>
            {meta.length > 0 && (
              <span className="text-muted-foreground truncate text-xs">
                {meta.join(' · ')}
              </span>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end">
            {balance ? (
              <>
                <span className="text-foreground text-sm font-medium tabular-nums">
                  {formatMoney(
                    Money.fromServer({
                      amount: toDisplayBalance(balance.balance, isCreditCard),
                      currency: account.currency,
                    }),
                  )}
                </span>
                {owedInMixedList && (
                  <span className="text-muted-foreground text-xs">owed</span>
                )}
                {account.__typename === 'DepositoryAccount' &&
                  account.currency !== 'USD' &&
                  balance.homeBalance != null && (
                    <span className="text-muted-foreground text-xs tabular-nums">
                      ≈{' '}
                      {Money.toDecimalString(
                        Money.fromServer({
                          // Same display-sign convention as the native line
                          // above (credit cards render debt as positive
                          // "owed").
                          amount: toDisplayBalance(
                            balance.homeBalance,
                            isCreditCard,
                          ),
                          currency: 'USD',
                        }),
                      )}
                    </span>
                  )}
                {account.__typename === 'DepositoryAccount' &&
                  balance.balanceStaleSince && (
                    <span className="text-muted-foreground text-xs">
                      as of {formatListDate(balance.balanceStaleSince)}
                    </span>
                  )}
                {observationAgeDays != null &&
                  balance.balanceAsOf != null &&
                  observationAgeDays > OBSERVATION_NOTE_DAYS && (
                    <span
                      className={
                        observationAgeDays > OBSERVATION_STALE_DAYS
                          ? 'text-xs text-amber-600 dark:text-amber-500'
                          : 'text-muted-foreground text-xs'
                      }
                    >
                      as of {formatListDate(balance.balanceAsOf)}
                    </span>
                  )}
              </>
            ) : (
              // Balances stream in after the metadata renders — hold the cell
              // with a quiet placeholder sized like the amount text.
              <span
                aria-hidden="true"
                className="bg-muted h-5 w-20 animate-pulse rounded"
              />
            )}
          </div>
        </Link>
        {onToggleStar && (
          <Button
            variant="ghost"
            onClick={handleToggleStar}
            className={`-mr-1 size-8 shrink-0 rounded-md p-1.5 hover:bg-foreground/10 ${
              dragHandle ? '' : 'hidden sm:flex'
            }`}
            aria-label={starred ? 'Unstar account' : 'Star account'}
          >
            <Star
              className={
                starred
                  ? 'size-4 fill-amber-500 text-amber-500'
                  : 'text-muted-foreground/40 hover:text-foreground size-4'
              }
            />
          </Button>
        )}
      </div>
    </div>
  );
};
